import { format, parseISO } from "date-fns";

// An invoice bills exactly one subject: an appointment, or a Property Contract
// (daily_work) calendar-month period (SHY-182). Every place that renders an
// invoice's "what/where/who" goes through this helper so contract invoices are
// never shown as a blank appointment.

export type InvoiceSubjectKind = "appointment" | "contract";

export interface InvoiceSubjectAppointmentData {
  id: string;
  individual_payment?: number | null;
  subs_payment?: number | null;
  property?: { id: string; name: string } | null;
  unit?: { unit_number: string } | null;
  service?: { name: string; parent?: { name: string } | null } | null;
  assigned_user?: { name: string } | null;
}

export interface InvoiceSubjectContractData {
  id: string;
  start_date?: string | null;
  end_date?: string | null;
  property?: { id: string; name: string } | null;
}

export interface InvoiceSubjectRelations {
  appointment_data?: InvoiceSubjectAppointmentData | null;
  contract_data?: InvoiceSubjectContractData | null;
}

export interface InvoiceSubjectSource extends InvoiceSubjectRelations {
  appointment?: string | null;
  daily_work?: string | null;
  period_start?: string | null;
  period_end?: string | null;
  issue_date?: string | null;
  amount?: number;
}

export interface InvoiceSubjectPresentation {
  referenceLabel: string;
  billedForLabel: string | null;
  referenceAction: "route" | "quick_view";
  referenceSuffix: string | null;
  detailServiceTitle: string | null;
  detailServiceDescription: string | null;
  detailLinkLabel: string | null;
  pdfServiceDetail: string | null;
}

export interface InvoiceSubjectQuickBooks {
  itemName: string;
  serviceDate: string | null;
  omitZeroPricedLineItems: readonly string[];
}

export interface InvoiceSubject {
  kind: InvoiceSubjectKind;
  /** Route to the thing being billed (appointment or contract detail). */
  href: string | null;
  /** Short id of the thing being billed, for compact tables. */
  shortId: string | null;
  propertyId: string | null;
  propertyName: string | null;
  unitNumber: string | null;
  /** Primary label: service name, or "Property Contract". */
  serviceName: string | null;
  /** Secondary label: parent service, or the billed period. */
  serviceDetail: string | null;
  assignedName: string | null;
  /** "Jul 2026" for contract invoices, null otherwise. */
  periodLabel: string | null;
  /** "Jul 1 – Jul 31, 2026" for contract invoices, null otherwise. */
  periodRange: string | null;
  presentation: InvoiceSubjectPresentation;
  invoiceLevelWorkerCosts: boolean;
  quickBooks: InvoiceSubjectQuickBooks;
}

export interface InvoiceProfitability {
  contractorPaid: number;
  grossProfit: number;
}

function safeParse(date: string | null | undefined): Date | null {
  if (!date) return null;
  const parsed = parseISO(date);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function formatPeriodLabel(periodStart: string | null | undefined): string | null {
  const start = safeParse(periodStart);
  return start ? format(start, "MMM yyyy") : null;
}

export function formatPeriodRange(
  periodStart: string | null | undefined,
  periodEnd: string | null | undefined
): string | null {
  const start = safeParse(periodStart);
  const end = safeParse(periodEnd);
  if (!start || !end) return null;
  return `${format(start, "MMM d")} – ${format(end, "MMM d, yyyy")}`;
}

export function getInvoiceSubject(invoice: InvoiceSubjectSource): InvoiceSubject {
  const contract = invoice.contract_data ?? null;
  const isContract = Boolean(invoice.daily_work || contract);

  if (isContract) {
    const contractId = contract?.id ?? invoice.daily_work ?? null;
    const periodLabel = formatPeriodLabel(invoice.period_start);
    const periodRange = formatPeriodRange(invoice.period_start, invoice.period_end);
    return {
      kind: "contract",
      href: contractId ? `/dailywork/${contractId}` : null,
      shortId: contractId ? contractId.slice(0, 8) : null,
      propertyId: contract?.property?.id ?? null,
      propertyName: contract?.property?.name ?? null,
      unitNumber: null,
      serviceName: "Property Contract",
      serviceDetail: periodLabel ? `Billing period ${periodLabel}` : null,
      assignedName: null,
      periodLabel,
      periodRange,
      presentation: {
        referenceLabel: "Property Contract",
        billedForLabel: contractId ? `Contract ${contractId.slice(0, 8)}` : "Contract",
        referenceAction: "route",
        referenceSuffix: periodLabel,
        detailServiceTitle: "Property Contract",
        detailServiceDescription: periodRange ? `Billing period: ${periodRange}` : null,
        detailLinkLabel: "View contract schedule",
        pdfServiceDetail: periodRange,
      },
      invoiceLevelWorkerCosts: false,
      quickBooks: {
        itemName: periodLabel ? `Property Contract ${periodLabel}` : "Property Contract",
        serviceDate: invoice.period_end ?? null,
        omitZeroPricedLineItems: ["Work summary"],
      },
    };
  }

  const appointment = invoice.appointment_data ?? null;
  const appointmentId = appointment?.id ?? invoice.appointment ?? null;
  return {
    kind: "appointment",
    href: appointmentId ? `/appointments/${appointmentId}` : null,
    shortId: appointmentId ? appointmentId.slice(0, 8) : null,
    propertyId: appointment?.property?.id ?? null,
    propertyName: appointment?.property?.name ?? null,
    unitNumber: appointment?.unit?.unit_number ?? null,
    serviceName: appointment?.service?.name ?? null,
    serviceDetail: appointment?.service?.parent?.name ?? null,
    assignedName: appointment?.assigned_user?.name ?? null,
    periodLabel: null,
    periodRange: null,
    presentation: {
      referenceLabel: "Appointment ID",
      billedForLabel: appointmentId ? appointmentId.slice(0, 8) : null,
      referenceAction: "quick_view",
      referenceSuffix: null,
      detailServiceTitle: appointment?.service?.name ?? null,
      detailServiceDescription: appointment?.service?.parent?.name ?? null,
      detailLinkLabel: null,
      pdfServiceDetail: appointment?.service?.parent?.name ?? null,
    },
    invoiceLevelWorkerCosts: true,
    quickBooks: {
      itemName: appointment?.service?.name ?? "Service",
      serviceDate: invoice.issue_date ?? null,
      omitZeroPricedLineItems: [],
    },
  };
}

const paysAreKnown = (appointment: InvoiceSubjectAppointmentData | null | undefined): appointment is InvoiceSubjectAppointmentData =>
  appointment != null && (appointment.individual_payment !== undefined || appointment.subs_payment !== undefined);

export function getInvoiceProfitability(invoice: InvoiceSubjectSource): InvoiceProfitability | null {
  const subject = getInvoiceSubject(invoice);
  const appointment = invoice.appointment_data;
  if (!subject.invoiceLevelWorkerCosts || !paysAreKnown(appointment)) return null;

  const contractorPaid = (appointment.individual_payment ?? 0) + (appointment.subs_payment ?? 0);
  return {
    contractorPaid,
    grossProfit: (invoice.amount ?? 0) - contractorPaid,
  };
}
